import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, fmtMoney, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gift, Clock, CheckCircle2, XCircle, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const OFFER_TYPES = ["Cashback", "Discount", "Reward Points", "EMI", "Lounge Access", "Travel", "Dining", "Fuel", "Other"];

function offerState(o) {
    if (o.used) return { label: "Used", tone: "bg-stone-100 text-stone-700 border-stone-200", Icon: CheckCircle2 };
    if (o.expired) return { label: "Expired", tone: "bg-stone-100 text-stone-500 border-stone-200", Icon: XCircle };
    if (o.expiring_soon) return { label: "Expires soon", tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock };
    return { label: "Active", tone: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 };
}

/** Live offers/benefits attached to a specific card.
 *
 *  Fetches its own data (so callers only pass `cardId`). Shows a friendly
 *  empty-state that links to /offers so users can attach one from the library.
 *  Same visual language as the Home "Use it or lose it" card. */
export default function CardOffersSection({ cardId }) {
    const { hasRole } = useAuth();
    const [offers, setOffers] = useState(null); // null = loading, [] = loaded empty
    const [addOpen, setAddOpen] = useState(false);

    const load = () => {
        if (!cardId) return;
        api.get("/offers", { params: { card_id: cardId } })
            .then((r) => setOffers(r.data || []))
            .catch(() => setOffers([]));
    };
    useEffect(load, [cardId]);
    const canManage = hasRole("admin", "finance");

    if (offers === null) return null;

    const active = offers.filter((o) => !o.used && !o.expired);
    const rest = offers.filter((o) => o.used || o.expired);

    return (
        <div className="mb-6" data-testid="card-benefits-live">
            <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
                <div>
                    <div className="eyebrow flex items-center gap-1.5">
                        <Gift size={11} /> Live offers &amp; benefits on this card
                    </div>
                    <div className="text-xs text-stone-500 mt-1">
                        {offers.length === 0
                            ? "No offers attached to this card yet."
                            : `${active.length} active${rest.length ? ` · ${rest.length} used or expired` : ""}`}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {canManage && (
                        <button
                            type="button"
                            onClick={() => setAddOpen(true)}
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-stone-700 hover:text-stone-900 border border-stone-200 hover:border-stone-900 rounded-full px-3 py-1.5 transition-colors"
                            data-testid="card-benefits-add-btn"
                        >
                            <Plus size={11} /> Add offer
                        </button>
                    )}
                    <Link
                        to="/offers"
                        className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-stone-900 transition-colors"
                        data-testid="card-benefits-manage-link"
                    >
                        Manage →
                    </Link>
                </div>
            </div>

            {offers.length === 0 ? (
                <div className="rounded-lg border border-dashed border-stone-200 bg-stone-50/60 p-4 text-center"
                     data-testid="card-benefits-empty">
                    <div className="text-sm text-stone-500">
                        No offers attached to this card yet.{" "}
                        {canManage ? (
                            <button
                                type="button"
                                onClick={() => setAddOpen(true)}
                                className="text-stone-900 underline underline-offset-2 hover:no-underline"
                                data-testid="card-benefits-attach-link"
                            >
                                Add one →
                            </button>
                        ) : (
                            <Link to="/offers" className="text-stone-900 underline underline-offset-2 hover:no-underline">
                                Attach one from the library →
                            </Link>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[...active, ...rest].map((o) => {
                        const st = offerState(o);
                        return (
                            <div key={o.id}
                                 className="rounded-lg border border-stone-200 bg-white p-4"
                                 data-testid={`card-benefit-${o.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-stone-900 leading-tight truncate">{o.title}</div>
                                        <div className="text-[11px] text-stone-500 mt-0.5">{o.type}</div>
                                    </div>
                                    <span className={`inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider border rounded px-1.5 py-0.5 shrink-0 ${st.tone}`}>
                                        <st.Icon size={10} /> {st.label}
                                    </span>
                                </div>
                                {o.description && (
                                    <div className="text-[12px] text-stone-600 mt-2 line-clamp-2">{o.description}</div>
                                )}
                                <div className="mt-3 flex items-center justify-between text-[11px]">
                                    <div className="text-stone-500 tabular-nums">
                                        {o.end_date
                                            ? `Expires ${fmtDate(o.end_date)}`
                                            : (o.recurring ? "Recurring · no expiry" : "No expiry set")}
                                    </div>
                                    {o.max_benefit_inr ? (
                                        <div className="tabular-nums font-medium text-stone-800">
                                            Up to {fmtMoney(o.max_benefit_inr)}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <QuickAddOfferDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                cardId={cardId}
                onCreated={() => { setAddOpen(false); load(); }}
            />
        </div>
    );
}

const EMPTY_OFFER = {
    title: "", type: "Cashback", description: "",
    start_date: "", end_date: "",
    max_benefit_inr: "", min_spend_inr: "", coupon_code: "",
};

function QuickAddOfferDialog({ open, onOpenChange, cardId, onCreated }) {
    const [f, setF] = useState(EMPTY_OFFER);
    const [busy, setBusy] = useState(false);
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

    useEffect(() => { if (open) setF(EMPTY_OFFER); }, [open]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                card_id: cardId,
                title: f.title.trim(),
                type: f.type,
                description: f.description || null,
                start_date: f.start_date || null,
                end_date: f.end_date || null,
                max_benefit_inr: f.max_benefit_inr === "" ? null : Number(f.max_benefit_inr),
                min_spend_inr: f.min_spend_inr === "" ? null : Number(f.min_spend_inr),
                coupon_code: f.coupon_code || null,
            };
            await api.post("/offers", payload);
            toast.success("Offer added");
            onCreated?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="card-benefits-add-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Add offer to this card</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div><Label className="eyebrow">Title</Label>
                        <Input required value={f.title} onChange={setInput("title")} className="mt-1.5"
                               placeholder="e.g. 5% cashback on Amazon Prime Day" data-testid="add-offer-title" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="eyebrow">Type</Label>
                            <Select value={f.type} onValueChange={set("type")}>
                                <SelectTrigger className="mt-1.5" data-testid="add-offer-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{OFFER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label className="eyebrow">Max Benefit (₹)</Label>
                            <Input type="number" value={f.max_benefit_inr} onChange={setInput("max_benefit_inr")}
                                   className="mt-1.5" placeholder="Optional" />
                        </div>
                        <div><Label className="eyebrow">Start Date</Label>
                            <Input type="date" value={f.start_date} onChange={setInput("start_date")} className="mt-1.5" />
                        </div>
                        <div><Label className="eyebrow">End Date</Label>
                            <Input type="date" value={f.end_date} onChange={setInput("end_date")} className="mt-1.5"
                                   data-testid="add-offer-end" />
                        </div>
                        <div><Label className="eyebrow">Min Spend (₹)</Label>
                            <Input type="number" value={f.min_spend_inr} onChange={setInput("min_spend_inr")}
                                   className="mt-1.5" placeholder="Optional" />
                        </div>
                        <div><Label className="eyebrow">Coupon Code</Label>
                            <Input value={f.coupon_code} onChange={setInput("coupon_code")} className="mt-1.5" placeholder="Optional" />
                        </div>
                    </div>
                    <div><Label className="eyebrow">Description</Label>
                        <Textarea rows={2} value={f.description} onChange={setInput("description")} className="mt-1.5"
                                  placeholder="Terms, exclusions, redemption steps…" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" disabled={busy || !f.title.trim()} data-testid="add-offer-save">
                            {busy ? "Saving…" : "Save offer"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
